import {
  cloneData,
  DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData,
} from '../dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { PaymentProvider } from './payment-provider.js';
import {
  fromEnumAsString, toEnumFromString,
} from '../../serialization/enum-utils.js';
import { VersionRange } from '../core/version-range.js';
import { PaymentAccountRef } from './payment-account-ref.js';
import { OfflineReceiptType } from './invoicing/offline-receipt-type.js';

export class OfflineTransactionDetails extends DataModel {
  static readonly TYPE_ID: string = '2ab25ac034cd474585d54f9d79370d29';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly provider: PaymentProvider,
    public readonly allowReceipts: boolean,
    public readonly receiptGroupId: string,
    public readonly paymentAccount: PaymentAccountRef,
    public readonly receiptType: OfflineReceiptType,
  ) {
    super();
  }

  get $typeId() {
    return OfflineTransactionDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OfflineTransactionDetails.SCHEMA_VERSION);
  }

  static create(options: {
    provider: PaymentProvider,
    allowReceipts: boolean,
    receiptGroupId: string,
    paymentAccount: PaymentAccountRef,
    receiptType: OfflineReceiptType,
  }): OfflineTransactionDetails {
    return new OfflineTransactionDetails(
        options.provider,
        options.allowReceipts,
        options.receiptGroupId,
        options.paymentAccount,
        options.receiptType,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineTransactionDetails {
    return OfflineTransactionDetails.create({
      provider: toEnumFromString(MapUtils.getString(map, 'provider'), PaymentProvider),
      allowReceipts: MapUtils.getBoolean(map, 'allowReceipts'),
      receiptGroupId: MapUtils.getString(map, 'receiptGroupId'),
      paymentAccount: context.fromData<PaymentAccountRef>(MapUtils.getRecord(map, 'paymentAccount')),
      receiptType: toEnumFromString(MapUtils.getString(map, 'receiptType'), OfflineReceiptType),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'provider': fromEnumAsString(this.provider),
      'allowReceipts': this.allowReceipts,
      'receiptGroupId': this.receiptGroupId,
      'paymentAccount': context.toData(this.paymentAccount),
      'receiptType': fromEnumAsString(this.receiptType),
    };
  }

  clone(): this {
    return new OfflineTransactionDetails(
        this.provider,
        this.allowReceipts,
        this.receiptGroupId,
        cloneData(this.paymentAccount),
        this.receiptType,
    ) as this;
  }

  isSame(other: OfflineTransactionDetails): boolean {
    if(this === other) {
      return true;
    }

    return this.provider === other.provider &&
      this.allowReceipts === other.allowReceipts &&
      this.receiptGroupId === other.receiptGroupId &&
      this.receiptType === other.receiptType &&
      isSameData(other.paymentAccount, this.paymentAccount);
  }

  copyWith(data: Partial<OfflineTransactionDetails>): OfflineTransactionDetails {
    return new OfflineTransactionDetails(
        data.provider ?? this.provider,
        data.allowReceipts ?? this.allowReceipts,
        data.receiptGroupId ?? this.receiptGroupId,
        data.paymentAccount ?? cloneData(this.paymentAccount),
        data.receiptType ?? this.receiptType,
    );
  }
}

export class OfflineTransactionDetailsBuilder extends DataModelBuilder<OfflineTransactionDetails> {
  build(context: SerializationContext, map: SerializedData): OfflineTransactionDetails {
    return OfflineTransactionDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OfflineTransactionDetails.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OfflineTransactionDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `receiptType` of type `OfflineReceiptType`.
    if(version < 2) {
      map['receiptType'] = fromEnumAsString(OfflineReceiptType.Receipt);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
